import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, MapPin, TrendingUp, Calendar, Award, Bike, Crown, CheckCircle, Camera, Upload, Zap, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import './ProfilePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, isPremium, upgradeToPremium, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [picturePreview, setPicturePreview] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/profile' } });
    }
  }, [isAuthenticated, navigate]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    const result = await upgradeToPremium();
    
    if (result.success) {
      setUpgradeSuccess(true);
      setTimeout(() => setUpgradeSuccess(false), 3000);
    } else {
      alert(t('auth.upgrade_failed') + ': ' + result.error);
    }
    
    setUpgrading(false);
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert(t('auth.image_only'));
      return;
    }

    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('auth.file_too_large'));
      return;
    }

    setUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload-profile-picture`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.url) {
        setPicturePreview(`${BACKEND_URL}${response.data.url}`);
        // Refresh user data to get new profile picture
        await refreshUser();
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert(t('auth.upload_error'));
    } finally {
      setUploadingPicture(false);
    }
  };

  if (!user) {
    return (
      <div className="loading-container" data-testid="profile-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  const achievements = [
    { 
      icon: <Award size={32} />, 
      title: 'Erste Tour',
      desc: 'Abgeschlossen',
      unlocked: user.total_rides > 0
    },
    { 
      icon: <MapPin size={32} />, 
      title: '100 km',
      desc: user.total_distance_km >= 100 ? 'Erreicht!' : `${Math.round(user.total_distance_km)}/100 km`,
      unlocked: user.total_distance_km >= 100
    },
    { 
      icon: <TrendingUp size={32} />, 
      title: '1000 Höhenmeter',
      desc: user.total_elevation_m >= 1000 ? 'Erreicht!' : `${Math.round(user.total_elevation_m)}/1000 m`,
      unlocked: user.total_elevation_m >= 1000
    },
    { 
      icon: <Calendar size={32} />, 
      title: '10 Touren',
      desc: user.total_rides >= 10 ? 'Erreicht!' : `${user.total_rides}/10 Touren`,
      unlocked: user.total_rides >= 10
    }
  ];

  return (
    <div className="profile-page" data-testid="profile-page">
      <header className="page-header">
        <h1 className="page-title" data-testid="profile-title">{t('profile.title')}</h1>
        <p className="page-subtitle">{t('profile.subtitle')}</p>
      </header>

      {/* Profile Info */}
      <div className="profile-info" data-testid="profile-info">
        <div className="profile-avatar-container">
          <div className="profile-avatar">
            {picturePreview || user.profile_picture ? (
              <img 
                src={picturePreview || `${BACKEND_URL}${user.profile_picture}`} 
                alt={user.name}
                className="profile-avatar-img"
              />
            ) : (
              <User size={48} />
            )}
          </div>
          <label className="profile-avatar-upload" htmlFor="profile-picture-input">
            <input
              id="profile-picture-input"
              type="file"
              accept="image/*"
              onChange={handleProfilePictureUpload}
              style={{ display: 'none' }}
            />
            {uploadingPicture ? (
              <Upload size={20} className="uploading" />
            ) : (
              <Camera size={20} />
            )}
          </label>
        </div>
        <div className="profile-details">
          <h2 className="profile-name">{user.name}</h2>
          <p className="profile-email">{user.email}</p>
          <div className="profile-badges">
            {user.subscription_tier === 'premium' || isPremium ? (
              <span className="badge premium-badge-large">
                <Crown size={16} />
                Premium
              </span>
            ) : user.subscription_tier === 'basic' ? (
              <span className="badge basic-badge-large">
                <Zap size={16} />
                Basic
              </span>
            ) : (
              <span className="badge free-badge">
                <Star size={16} />
                Free
              </span>
            )}
            <span className="badge">
              <Bike size={16} />
              {t(`bike_types.${user.bike_type}`)}
            </span>
            {user.ebike_battery_kwh && (
              <span className="badge">
                🔋 {user.ebike_battery_kwh} kWh
              </span>
            )}
            <span className="badge">
              🌍 {user.language.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Premium Upgrade Section */}
      {!isPremium && (
        <div className="premium-upgrade-card">
          <div className="premium-upgrade-content">
            <Crown size={48} className="premium-icon" />
            <div>
              <h2>{t('profile.upgrade_title')}</h2>
              <p>{t('profile.upgrade_desc')}</p>
              <ul>
                <li>✅ {t('profile.upgrade_feature1')}</li>
                <li>✅ {t('profile.upgrade_feature2')}</li>
                <li>✅ {t('profile.upgrade_feature3')}</li>
                <li>✅ {t('profile.upgrade_feature4')}</li>
                <li>✅ {t('profile.upgrade_feature5')}</li>
              </ul>
              <button 
                onClick={() => window.location.href = '/pricing'}
                className="premium-upgrade-btn"
              >
                {t('profile.upgrade_button')}
              </button>
              {upgradeSuccess && (
                <div className="upgrade-success">
                  <CheckCircle size={20} />
                  {t('profile.upgrade_success')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-distance">
          <div className="stat-icon">
            <MapPin size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{user.total_distance_km.toFixed(1)} km</div>
            <div className="stat-label">{t('profile.total_distance')}</div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-elevation">
          <div className="stat-icon">
            <TrendingUp size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{Math.round(user.total_elevation_m)} m</div>
            <div className="stat-label">{t('profile.total_elevation')}</div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-rides">
          <div className="stat-icon">
            <Calendar size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{user.total_rides}</div>
            <div className="stat-label">{t('profile.total_rides')}</div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <section className="achievements-section">
        <h2 className="section-title">{t('profile.achievements')}</h2>
        <div className="achievements-grid">
          <div className={`achievement-card ${user.total_rides > 0 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon"><Award size={32} /></div>
            <h3 className="achievement-title">{t('profile.first_tour')}</h3>
            <p className="achievement-desc">{t('profile.completed')}</p>
            {user.total_rides > 0 && <div className="achievement-badge">✓</div>}
          </div>
          <div className={`achievement-card ${user.total_distance_km >= 100 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon"><MapPin size={32} /></div>
            <h3 className="achievement-title">100 km</h3>
            <p className="achievement-desc">
              {user.total_distance_km >= 100 ? t('profile.reached') : `${Math.round(user.total_distance_km)}/100 km`}
            </p>
            {user.total_distance_km >= 100 && <div className="achievement-badge">✓</div>}
          </div>
          <div className={`achievement-card ${user.total_elevation_m >= 1000 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon"><TrendingUp size={32} /></div>
            <h3 className="achievement-title">1000 Höhenmeter</h3>
            <p className="achievement-desc">
              {user.total_elevation_m >= 1000 ? t('profile.reached') : `${Math.round(user.total_elevation_m)}/1000 m`}
            </p>
            {user.total_elevation_m >= 1000 && <div className="achievement-badge">✓</div>}
          </div>
          <div className={`achievement-card ${user.total_rides >= 10 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon"><Calendar size={32} /></div>
            <h3 className="achievement-title">10 Touren</h3>
            <p className="achievement-desc">
              {user.total_rides >= 10 ? t('profile.reached') : `${user.total_rides}/10 Touren`}
            </p>
            {user.total_rides >= 10 && <div className="achievement-badge">✓</div>}
          </div>
        </div>
      </section>

      {/* Info Box */}
      <div className="info-box" data-testid="demo-info">
        <p>
          <strong>{t('profile.demo_mode')}</strong>
        </p>
      </div>
    </div>
  );
}
